import Header from '@/react-app/components/Header';
import Footer from '@/react-app/components/Footer';
import { Cookie, Settings, BarChart, Shield, Target, Mail, Phone } from 'lucide-react';

export default function Cookies() {
    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            <Header />

            {/* Hero Section */}
            <section className="pt-16 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="text-center mb-12">
                        <div className="bg-blue-100 dark:bg-blue-900 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                            <Cookie className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                            Cookie Policy
                        </h1>
                        <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                            Learn how we use cookies and similar technologies to enhance your experience on our platform.
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                            Last updated: September 27, 2025
                        </p>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="prose prose-lg max-w-none dark:prose-invert">

                        {/* Introduction */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">What Are Cookies?</h2>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                                Cookies are small text files that are stored on your device (computer, tablet, smartphone) when you visit our website. They help us recognize your device and remember certain information about your preferences or past actions.
                            </p>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                This Cookie Policy explains how Trade Enrich ("we," "our," or "us") uses cookies and similar technologies on our website and services. By using our platform, you consent to our use of cookies as described in this policy.
                            </p>
                        </div>

                        {/* Types of Cookies */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Types of Cookies We Use</h2>

                            <div className="space-y-8">
                                {/* Essential Cookies */}
                                <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-xl border border-red-200 dark:border-red-800">
                                    <div className="flex items-start mb-4">
                                        <Shield className="h-6 w-6 text-red-600 dark:text-red-400 mr-3 mt-0.5" />
                                        <div>
                                            <h3 className="text-xl font-semibold text-red-800 dark:text-red-200 mb-2">Essential Cookies</h3>
                                            <p className="text-red-700 dark:text-red-300 mb-3">
                                                These cookies are necessary for our website to function properly and cannot be switched off. They are usually set in response to actions you take such as logging in, filling forms, or setting privacy preferences.
                                            </p>
                                            <div className="text-sm text-red-600 dark:text-red-400">
                                                <p><strong>Purpose:</strong> Authentication, security, remembering your preferences</p>
                                                <p><strong>Duration:</strong> Session or up to 1 year</p>
                                                <p><strong>Examples:</strong> Login tokens, CSRF protection, language preferences</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Analytics Cookies */}
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-xl border border-blue-200 dark:border-blue-800">
                                    <div className="flex items-start mb-4">
                                        <BarChart className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
                                        <div>
                                            <h3 className="text-xl font-semibold text-blue-800 dark:text-blue-200 mb-2">Analytics Cookies</h3>
                                            <p className="text-blue-700 dark:text-blue-300 mb-3">
                                                These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. We use this data to improve our services and user experience.
                                            </p>
                                            <div className="text-sm text-blue-600 dark:text-blue-400">
                                                <p><strong>Purpose:</strong> Usage analytics, performance monitoring, A/B testing</p>
                                                <p><strong>Duration:</strong> Up to 2 years</p>
                                                <p><strong>Examples:</strong> Google Analytics, page view tracking, session duration</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Functional Cookies */}
                                <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-xl border border-green-200 dark:border-green-800">
                                    <div className="flex items-start mb-4">
                                        <Settings className="h-6 w-6 text-green-600 dark:text-green-400 mr-3 mt-0.5" />
                                        <div>
                                            <h3 className="text-xl font-semibold text-green-800 dark:text-green-200 mb-2">Functional Cookies</h3>
                                            <p className="text-green-700 dark:text-green-300 mb-3">
                                                These cookies enhance the functionality of our website by remembering your choices and providing enhanced features and personalization.
                                            </p>
                                            <div className="text-sm text-green-600 dark:text-green-400">
                                                <p><strong>Purpose:</strong> Enhanced user experience, personalization, feature functionality</p>
                                                <p><strong>Duration:</strong> Session to 1 year</p>
                                                <p><strong>Examples:</strong> Theme preferences, dashboard layout, notification settings</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Marketing Cookies */}
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-xl border border-purple-200 dark:border-purple-800">
                                    <div className="flex items-start mb-4">
                                        <Target className="h-6 w-6 text-purple-600 dark:text-purple-400 mr-3 mt-0.5" />
                                        <div>
                                            <h3 className="text-xl font-semibold text-purple-800 dark:text-purple-200 mb-2">Marketing Cookies</h3>
                                            <p className="text-purple-700 dark:text-purple-300 mb-3">
                                                These cookies track your browsing habits to enable us to show advertising which is more likely to be of interest to you. They may be used to build a profile of your interests and show relevant ads on other sites.
                                            </p>
                                            <div className="text-sm text-purple-600 dark:text-purple-400">
                                                <p><strong>Purpose:</strong> Targeted advertising, conversion tracking, remarketing</p>
                                                <p><strong>Duration:</strong> Up to 2 years</p>
                                                <p><strong>Examples:</strong> Google Ads, Facebook Pixel, LinkedIn Insight Tag</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* How We Use Cookies */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">How We Use Cookies</h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">We use cookies for various purposes to enhance your experience:</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Account Management</h4>
                                    <ul className="text-gray-600 dark:text-gray-300 space-y-2 text-sm">
                                        <li>• Keep you logged in during your session</li>
                                        <li>• Remember your security preferences</li>
                                        <li>• Provide secure access to your financial data</li>
                                        <li>• Enable two-factor authentication</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">User Experience</h4>
                                    <ul className="text-gray-600 dark:text-gray-300 space-y-2 text-sm">
                                        <li>• Remember your language and currency preferences</li>
                                        <li>• Maintain your dashboard customizations</li>
                                        <li>• Save your notification settings</li>
                                        <li>• Provide personalized content recommendations</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Analytics & Improvement</h4>
                                    <ul className="text-gray-600 dark:text-gray-300 space-y-2 text-sm">
                                        <li>• Analyze how users interact with our platform</li>
                                        <li>• Identify popular features and content</li>
                                        <li>• Monitor system performance and errors</li>
                                        <li>• Conduct A/B tests for improvements</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Security & Fraud Prevention</h4>
                                    <ul className="text-gray-600 dark:text-gray-300 space-y-2 text-sm">
                                        <li>• Detect and prevent fraudulent activities</li>
                                        <li>• Monitor for suspicious behavior patterns</li>
                                        <li>• Implement rate limiting and DDoS protection</li>
                                        <li>• Verify user identity and location</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Third-Party Cookies */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Third-Party Cookies</h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">
                                We also use cookies from trusted third-party services to enhance our platform's functionality:
                            </p>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-gray-800">
                                            <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left text-gray-900 dark:text-white font-semibold">Service</th>
                                            <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left text-gray-900 dark:text-white font-semibold">Purpose</th>
                                            <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left text-gray-900 dark:text-white font-semibold">Duration</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300">Google Analytics</td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300">Website analytics and user behavior tracking</td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300">2 years</td>
                                        </tr>
                                        <tr>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300">Stripe</td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300">Payment processing and fraud detection</td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300">Session</td>
                                        </tr>
                                        <tr>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300">Intercom</td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300">Customer support and live chat</td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300">6 months</td>
                                        </tr>
                                        <tr>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300">Hotjar</td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300">User experience analysis and heatmaps</td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-600 dark:text-gray-300">1 year</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Managing Cookies */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Managing Your Cookie Preferences</h2>

                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Browser Settings</h3>
                                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                                        Most web browsers allow you to control cookies through their settings. You can:
                                    </p>
                                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 ml-4">
                                        <li>Block all cookies from being set</li>
                                        <li>Allow only first-party cookies</li>
                                        <li>Delete existing cookies</li>
                                        <li>Receive notifications when cookies are being set</li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Cookie Consent Manager</h3>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        When you first visit our website, you'll see a cookie consent banner where you can choose which types of cookies to accept. You can update your preferences at any time by clicking the "Cookie Settings" link in our website footer.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Opt-Out Links</h3>
                                    <p className="text-gray-600 dark:text-gray-300 mb-3">
                                        You can opt out of specific third-party cookies using these links:
                                    </p>
                                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 ml-4">
                                        <li><a href="https://tools.google.com/dlpage/gaoptout" className="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a></li>
                                        <li><a href="https://www.facebook.com/settings?tab=ads" className="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">Facebook Ads Opt-out</a></li>
                                        <li><a href="https://optout.aboutads.info/" className="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance Opt-out</a></li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Impact of Disabling Cookies */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Impact of Disabling Cookies</h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">
                                While you can disable cookies, please note that doing so may affect your experience on our platform:
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                                    <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Essential Features Affected</h4>
                                    <ul className="text-amber-700 dark:text-amber-300 text-sm space-y-1">
                                        <li>• Inability to stay logged in</li>
                                        <li>• Loss of security features</li>
                                        <li>• Payment processing issues</li>
                                        <li>• Form submission problems</li>
                                    </ul>
                                </div>

                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Enhanced Features Affected</h4>
                                    <ul className="text-blue-700 dark:text-blue-300 text-sm space-y-1">
                                        <li>• Personalized recommendations</li>
                                        <li>• Saved preferences and settings</li>
                                        <li>• Customized dashboard layouts</li>
                                        <li>• Targeted support and help content</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Updates to Policy */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Updates to This Policy</h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                We may update this Cookie Policy from time to time to reflect changes in our practices, technology, or legal requirements. We will notify you of any significant changes by posting a notice on our website or sending you an email notification. Your continued use of our services after such changes constitutes acceptance of the updated policy.
                            </p>
                        </div>

                        {/* Contact Information */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                                <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                                Contact Us About Cookies
                            </h2>
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                                <p className="text-gray-600 dark:text-gray-300 mb-4">
                                    If you have any questions about our use of cookies or this Cookie Policy, please contact us:
                                </p>
                                <div className="space-y-2 text-gray-600 dark:text-gray-300">
                                    <div className="flex items-center">
                                        <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                                        <span>Email: support@tradeenrich.com</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                                        <span>Phone: +234 7063441554</span>
                                    </div>
                                    <div className="mt-4">
                                        <p className="font-medium">Mailing Address:</p>
                                        <p>Trade Enrich Privacy Team<br />
                                            Thomas and Paulina Ibe's New Compound by Nkwo Mbaise Market Umuodo Itu Autonomous Community Ezinihitte-Mbaise LGA, Imo State, Nigeria<br />
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
